
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
    Box, Typography, Grid, Paper, CircularProgress, Card, CardContent,
    List, ListItem, ListItemIcon, ListItemText, Divider, Avatar, Chip, Skeleton,
    useTheme, useMediaQuery, ButtonBase, Table, TableBody, TableCell, TableHead, TableRow,
    TableContainer, Checkbox, FormControlLabel, Dialog, DialogTitle, DialogContent,
    DialogActions, Button, IconButton
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ListAltIcon from '@mui/icons-material/ListAlt';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TodayIcon from '@mui/icons-material/Today';
import AssessmentIcon from '@mui/icons-material/Assessment';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import CloseIcon from '@mui/icons-material/Close';
import InventoryIcon from '@mui/icons-material/Inventory';
import EmailIcon from '@mui/icons-material/Email';
import PaymentIcon from '@mui/icons-material/Payment';
import ForumIcon from '@mui/icons-material/Forum';
import GroupIcon from '@mui/icons-material/Group';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import TableChartIcon from "@mui/icons-material/TableChart";
import PeopleIcon from '@mui/icons-material/People';
import { useAuth } from '../components/AuthContext';
import StaffSelector from '../components/StaffSelector';
import { fetchDispatchReservations, fetchSquareInvoicesForReservations, fetchSquareInvoicesBulk, mergeStaffPriorities } from '../api/dispatchService';
import { apiFetchJson } from '../api/dispatchClient';
import { SYNC_PAST_DAYS, SYNC_FUTURE_DAYS } from '../constants/calendar';
import { isOnDuty, isFixedStaff, isSystemAccount } from '../utils/staffPriority';
import { isLineEnabled, isChatBoardEnabled } from '../config/backend';

import DashboardSidebar from '../components/DashboardSidebar';

dayjs.extend(isBetween);

interface StatCardProps {
    title: string;
    value: string;
    subtitle?: string;
    icon: React.ReactNode;
    color?: string;
    gradient?: string;
}

const StatCard: React.FC<StatCardProps> = ({ title, value, subtitle, icon, color, gradient }) => {
    return (
        <Card sx={{
            height: '100%',
            borderRadius: 4,
            boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.07)',
            background: gradient || '#fff',
            position: 'relative',
            overflow: 'hidden',
            border: 'none'
        }}>
            <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ color: gradient ? 'rgba(255,255,255,0.8)' : 'text.secondary', fontWeight: 600 }}>
                        {title}
                    </Typography>
                    <Box sx={{
                        p: 1,
                        borderRadius: 2,
                        backgroundColor: gradient ? 'rgba(255,255,255,0.2)' : `${color}20`,
                        color: gradient ? '#fff' : color
                    }}>
                        {icon}
                    </Box>
                </Box>
                <Typography variant="h4" sx={{ fontWeight: 800, mb: 0.5, color: gradient ? '#fff' : 'text.primary' }}>
                    {value}
                </Typography>
                {subtitle && (
                    <Typography variant="caption" sx={{ color: gradient ? 'rgba(255,255,255,0.7)' : 'text.secondary' }}>
                        {subtitle}
                    </Typography>
                )}
            </CardContent>
        </Card>
    );
};

const Dashboard: React.FC = () => {
    const { user, logout, useMockAuth } = useAuth();
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const isAdmin = user?.level === 1;

    const [dataLoading, setDataLoading] = useState<boolean>(true);
    const [reservations, setReservations] = useState<any[]>([]);
    const [squareInvoices, setSquareInvoices] = useState<Record<string, any[]>>({});
    const [debugInfo, setDebugInfo] = useState<{ count: number; range: string; squareContacts: number }>({ count: 0, range: '', squareContacts: 0 });
    const [onlyValidResponses, setOnlyValidResponses] = useState<boolean>(true);
    const [modalData, setModalData] = useState<{ open: boolean; date: string; list: any[] }>({ open: false, date: '', list: [] });

    // 非管理者向け: スタッフ一覧
    const [staffList, setStaffList] = useState<any[]>([]);
    const [staffLoading, setStaffLoading] = useState<boolean>(false);

    // データの取得（管理者のみ売上/反響データを取得）
    const loadData = useCallback(async () => {
        setDataLoading(true);
        try {
            const now = dayjs();
            // 共通同期ウィンドウに統一（約8ヶ月前〜3年後）。当月稼働分の絞り込みは
            // 下記 targetMonthReservations でクライアント側に行うため、範囲拡大でも表示は不変。
            const rangeStart = now.subtract(SYNC_PAST_DAYS, 'day').format('YYYY-MM-DD');
            const rangeEnd = now.add(SYNC_FUTURE_DAYS, 'day').format('YYYY-MM-DD');
            setDebugInfo(prev => ({ ...prev, range: `${rangeStart} ~ ${rangeEnd}` }));

            const resData = await fetchDispatchReservations({
                start_date: rangeStart,
                end_date: rangeEnd
            });

            const reservationsList = resData || [];
            setReservations(reservationsList);
            setDebugInfo(prev => ({ ...prev, count: reservationsList.length }));

            // Square情報が必要な予約の抽出 (現在売上に影響するもの = 今月稼働中の完了/利用中予約)
            // status: done(2), in_use(3)
            const isTargetForSales = (status: any) => {
                const s = String(status).toLowerCase();
                return ['2', '3', 'done', 'in_use'].includes(s);
            };

            const targetMonthReservations = reservationsList.filter((r: any) => {
                if (!isTargetForSales(r.status)) return false;
                const start = dayjs(r.start_date);
                const end = dayjs(r.end_date);
                // 今月に重なっているかどうか
                return start.isValid() && end.isValid() &&
                    start.isBefore(dayjs().endOf('month')) &&
                    end.isAfter(dayjs().startOf('month'));
            });

            const uniqueTargetIds = [...new Set(targetMonthReservations.map((r: any) => r.contact_code || r.detail?.contact_id).filter(Boolean))];

            // 最適化: まず直近のPAIDインボイスを一括取得(100件)して大半をカバーする
            const bulkInvoices = await fetchSquareInvoicesBulk({
                invoice_status: 'PAID',
                per_page: 100,
                sort_by: 'invoice_created_at',
                sort_order: 'desc'
            });

            const sqMap: Record<string, any[]> = {};
            (bulkInvoices.data || []).forEach((inv: any) => {
                const cid = inv.contact_id;
                if (cid) {
                    if (!sqMap[cid]) sqMap[cid] = [];
                    sqMap[cid].push(inv);
                }
            });

            // カバーできていないIDのみ個別取得
            const missingIds = uniqueTargetIds.filter((id: any) => !sqMap[id]);

            if (missingIds.length > 0) {
                // 万が一大量にある場合に備え、上位50件に制限（通常は数件のはず）
                const limitedMissingIds = missingIds.slice(0, 50);
                const additionalSqData = await fetchSquareInvoicesForReservations(limitedMissingIds);

                // マージ
                Object.entries(additionalSqData).forEach(([cid, invs]: [string, any]) => {
                    sqMap[cid] = invs;
                });
            }

            setSquareInvoices(sqMap);
            setDebugInfo(prev => ({ ...prev, squareContacts: uniqueTargetIds.length }));

        } catch (error) {
            console.error('Failed to load dashboard data:', error);
        } finally {
            setDataLoading(false);
        }
    }, []);

    // 非管理者向け: スタッフ一覧取得
    const loadStaff = useCallback(async () => {
        setStaffLoading(true);
        try {
            const response = await apiFetchJson('staff?per_page=100');
            if (response?.data) {
                const active = response.data.filter((s: any) => !s.is_deleted && s.is_active !== 0 && !isSystemAccount(s));
                const merged = mergeStaffPriorities(active);
                merged.sort((a: any, b: any) => (b.priority || 0) - (a.priority || 0));
                setStaffList(merged);
            }
        } catch (error) {
            console.error('Failed to load staff list:', error);
        } finally {
            setStaffLoading(false);
        }
    }, []);

    useEffect(() => {
        if (isAdmin) {
            loadData();
        } else {
            loadStaff();
        }
    }, [isAdmin, loadData, loadStaff]);

    useEffect(() => {
        document.title = 'ダッシュボード | レンタカー予約カレンダー';
    }, []);

    const stats = useMemo(() => {
        const now = dayjs();
        const startOfToday = now.startOf('day');
        const endOfToday = now.endOf('day');
        const startOfThisMonth = now.startOf('month');
        const endOfThisMonth = now.endOf('month');

        // ヘルパー: ステータスが一致するか確認
        const isStatus = (r: any, targets: any[]) => {
            const s = String(r.status).toLowerCase();
            return targets.some(t => String(t).toLowerCase() === s);
        };

        // 1. 今日の売上 (Square)
        // 貸出済み=2, 利用中=3
        const todayReservations = reservations.filter((r: any) => {
            if (!isStatus(r, [2, 3, 'done', 'in_use'])) return false;
            const start = dayjs(r.start_date);
            const end = dayjs(r.end_date);
            return start.isValid() && end.isValid() && start.isBefore(endOfToday) && end.isAfter(startOfToday);
        });

        let todaySales = 0;
        const processedTodayInvIds = new Set<string>();
        todayReservations.forEach((r: any) => {
            const contactId = r.contact_code || r.detail?.contact_id;
            const invoices = squareInvoices[contactId] ?? [];
            invoices.forEach((inv: any) => {
                if (inv.invoice_status === 'PAID' && !processedTodayInvIds.has(inv.invoice_id)) {
                    todaySales += (inv.payment_amount || 0);
                    processedTodayInvIds.add(inv.invoice_id);
                }
            });
        });

        // 2. 今月の売上 (Square)
        const thisMonthStockReservations = reservations.filter((r: any) => {
            if (!isStatus(r, [2, 3, 'done', 'in_use'])) return false;
            const start = dayjs(r.start_date);
            const end = dayjs(r.end_date);
            return start.isValid() && end.isValid() && start.isBefore(endOfThisMonth) && end.isAfter(startOfThisMonth);
        });

        let thisMonthSales = 0;
        const processedMonthInvIds = new Set<string>();
        thisMonthStockReservations.forEach((r: any) => {
            const contactId = r.contact_code || r.detail?.contact_id;
            const invoices = squareInvoices[contactId] ?? [];
            invoices.forEach((inv: any) => {
                if (inv.invoice_status === 'PAID' && !processedMonthInvIds.has(inv.invoice_id)) {
                    thisMonthSales += (inv.payment_amount || 0);
                    processedMonthInvIds.add(inv.invoice_id);
                }
            });
        });

        // 3. 今月の売上予想 (Reservation sum_price)
        // 確定=1, 貸出済み=2, 利用中=3
        const expectedReservations = reservations.filter((r: any) => {
            if (!isStatus(r, [1, 2, 3, 'booked', 'done', 'in_use'])) return false;
            const start = dayjs(r.start_date);
            return start.isValid() && (start as any).isBetween(startOfThisMonth, endOfThisMonth, null, '[]');
        });
        const thisMonthExpectedSales = expectedReservations.reduce((sum: number, r: any) => sum + (Number(r.sum_price) || Number(r.detail?.sum_price) || 0), 0);


        // 4. 今月の反響 (booking_date)
        const responseReservations = reservations.filter((r: any) => {
            const targets = onlyValidResponses
                ? [1, 2, 3, 'booked', 'done', 'in_use']
                : [1, 2, 3, 6, 8, 'booked', 'done', 'in_use', 'full', 'mail_ready'];

            if (!isStatus(r, targets)) return false;
            const bDate = r.booking_date || r.created_at;
            if (!bDate) return false;
            const bookingDate = dayjs(bDate);
            return bookingDate.isValid() && (bookingDate as any).isBetween(startOfThisMonth, endOfThisMonth, null, '[]');
        });

        const mediaResponse: Record<string, { count: number; amount: number }> = {};
        responseReservations.forEach((r: any) => {
            const media = r.contact_media || r.detail?.contact_media || '不明';
            if (!mediaResponse[media]) {
                mediaResponse[media] = { count: 0, amount: 0 };
            }
            mediaResponse[media].count += 1;
            mediaResponse[media].amount += (Number(r.sum_price) || Number(r.detail?.sum_price) || 0);
        });

        const mediaArray = Object.entries(mediaResponse).map(([name, data]) => ({
            name,
            ...data
        })).sort((a, b) => b.amount - a.amount);

        // 5. 反響（デイリー）
        const daysInMonth = endOfThisMonth.date();
        const dailyResponseArray: any[] = [];
        for (let d = 1; d <= daysInMonth; d++) {
            const date = startOfThisMonth.date(d).startOf('day');
            const dayReservations = responseReservations.filter((r: any) => {
                const bDate = r.booking_date || r.created_at;
                return dayjs(bDate).isSame(date, 'day');
            });

            const amount = dayReservations.reduce((sum: number, r: any) => sum + (Number(r.sum_price) || Number(r.detail?.sum_price) || 0), 0);
            dailyResponseArray.push({
                date: date.format('M/D'),
                fullDate: date.format('YYYY年MM月DD日'),
                dayOfWeek: ['日', '月', '火', '水', '木', '金', '土'][date.day()],
                isWeekend: date.day() === 0 || date.day() === 6,
                isSunday: date.day() === 0,
                isSaturday: date.day() === 6,
                count: dayReservations.length,
                amount: amount,
                avg: dayReservations.length > 0 ? Math.round(amount / dayReservations.length) : 0,
                reservations: dayReservations // モーダル表示用
            });
        }

        return {
            todaySales,
            thisMonthSales,
            thisMonthExpectedSales,
            mediaArray,
            dailyResponseArray,
            responseCount: responseReservations.length,
            responseAmount: responseReservations.reduce((sum: number, r: any) => sum + (Number(r.sum_price) || Number(r.detail?.sum_price) || 0), 0)
        };
    }, [reservations, squareInvoices, onlyValidResponses]);

    // 非管理者向けツールメニュー（宮古インスタンスでは LINE を除外）
    const toolItems = [
        { label: '在庫管理', path: '/stock', icon: <InventoryIcon />, color: '#3b82f6' },
        { label: 'メール一斉送信', path: '/main_send', icon: <EmailIcon />, color: '#10b981' },
        { label: '決済確認', path: '/payment_check', icon: <PaymentIcon />, color: '#8b5cf6' },
        { label: 'データ整合', path: '/reconcile', icon: <CompareArrowsIcon />, color: '#f59e0b' },
        { label: '月間貸出返却予定', path: '/monthly-stats', icon: <TableChartIcon />, color: '#0f766e' },
        { label: '顧客', path: '/customers', icon: <PeopleIcon />, color: '#db2777' },
        // /line はチャット・ボード。LINE 連携が無い宮古でも自社チャット（予約者チャット）が並ぶので出す
        ...(isChatBoardEnabled() ? [{ label: isLineEnabled() ? 'LINEチャット' : 'チャット', path: '/line', icon: <ForumIcon />, color: '#06b6d4' }] : []),
    ];

    const onDutyStaff = staffList.filter(s => isOnDuty(s.priority) && !isFixedStaff(s));
    const offDutyStaff = staffList.filter(s => !isOnDuty(s.priority) && !isFixedStaff(s));

    return (
        <Box sx={{ display: 'flex', minHeight: '100vh', background: '#f8fafc' }}>
            {/* Sidebar */}
            <DashboardSidebar />

            {/* Main Content */}
            <Box component="main" sx={{ flexGrow: 1, p: { xs: 2, md: 4 }, pt: { xs: 7, md: 4 }, overflowY: 'auto' }}>
                <Box sx={{ mb: { xs: 2, md: 4 }, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 1 }}>
                    <Box>
                        <Typography variant={isMobile ? 'h5' : 'h4'} sx={{ fontWeight: 800, color: '#1e293b', mb: 0.5 }}>
                            ダッシュボード
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#64748b' }}>
                            {dayjs().format('YYYY年MM月DD日')} のビジネス状況
                        </Typography>
                        {isAdmin && (
                            <Typography variant="caption" sx={{ color: '#94a3b8', mt: 0.5, display: 'block' }}>
                                取得データ: {debugInfo.count}件 / Square照会: {debugInfo.squareContacts}件 ({debugInfo.range})
                            </Typography>
                        )}
                    </Box>
                    {isAdmin && (
                        <Box>
                            <ButtonBase
                                onClick={loadData}
                                sx={{
                                    bgcolor: '#fff',
                                    border: '1px solid #e2e8f0',
                                    borderRadius: 2,
                                    px: 2,
                                    py: 1,
                                    color: '#64748b',
                                    fontSize: '0.85rem',
                                    fontWeight: 600,
                                    boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                                    '&:hover': { bgcolor: '#f1f5f9', color: '#1e293b' }
                                }}
                            >
                                更新
                            </ButtonBase>
                        </Box>
                    )}
                </Box>

                {/* ===== 非管理者: ツールボタン + スタッフ一覧 ===== */}
                {!isAdmin && (
                    <>
                        {/* ツールメニュー */}
                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b', mb: 2 }}>
                            ツール
                        </Typography>
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' }, gap: 2, mb: 4 }}>
                            {toolItems.map((item) => (
                                <Paper
                                    key={item.path}
                                    elevation={1}
                                    onClick={() => navigate(item.path)}
                                    sx={{
                                        cursor: 'pointer',
                                        p: 3,
                                        borderRadius: 3,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        gap: 1,
                                        transition: 'transform 0.15s, box-shadow 0.15s',
                                        '&:hover': { transform: 'translateY(-2px)', boxShadow: 4 },
                                    }}
                                >
                                    <Box sx={{ color: item.color, fontSize: 40 }}>
                                        {React.cloneElement(item.icon, { sx: { fontSize: 40 } })}
                                    </Box>
                                    <Typography variant="body2" sx={{ fontWeight: 600, textAlign: 'center' }}>
                                        {item.label}
                                    </Typography>
                                </Paper>
                            ))}
                        </Box>

                        {/* 本日のスタッフ */}
                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <GroupIcon color="primary" /> 本日のスタッフ
                        </Typography>
                        {staffLoading ? (
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                {[1,2,3,4].map(i => <Skeleton key={i} variant="rounded" width={120} height={40} />)}
                            </Box>
                        ) : (
                            <Paper sx={{ borderRadius: 3, p: 3, mb: 4 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#16a34a', mb: 1.5 }}>
                                    本日出勤 ({onDutyStaff.length})
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 3 }}>
                                    {onDutyStaff.length === 0 ? (
                                        <Typography variant="body2" color="text.secondary">出勤スタッフがいません</Typography>
                                    ) : onDutyStaff.map((s: any) => (
                                        <Chip
                                            key={s.id}
                                            label={s.staff_name}
                                            sx={{
                                                fontWeight: 700,
                                                fontSize: '0.85rem',
                                                backgroundColor: (s.bg_color && String(s.bg_color).trim()) || '#e8f5e9',
                                                color: (s.text_color && String(s.text_color).trim()) || '#2e7d32',
                                                height: 36,
                                            }}
                                        />
                                    ))}
                                </Box>
                                <Divider sx={{ mb: 2 }} />
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#94a3b8', mb: 1 }}>
                                    オフ ({offDutyStaff.length})
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                    {offDutyStaff.map((s: any) => (
                                        <Chip key={s.id} label={s.staff_name} size="small" sx={{ opacity: 0.6 }} />
                                    ))}
                                </Box>
                            </Paper>
                        )}
                    </>
                )}

                {/* ===== 管理者: 売上・反響（既存、非同期対応） ===== */}
                {isAdmin && dataLoading && (
                    <Grid container spacing={{ xs: 1.5, md: 3 }} sx={{ mb: { xs: 3, md: 6 } }}>
                        {[1,2,3].map(i => (
                            <Grid item xs={12} sm={6} lg={4} key={i} {...{} as any}>
                                <Skeleton variant="rounded" height={140} sx={{ borderRadius: 4 }} />
                            </Grid>
                        ))}
                    </Grid>
                )}

                {isAdmin && !dataLoading && (
                <>
                <Grid container spacing={{ xs: 1.5, md: 3 }} sx={{ mb: { xs: 3, md: 6 } }}>
                    <Grid item xs={12} sm={6} lg={4} {...{} as any}>
                        <StatCard
                            title="今日の売上 (Square)"
                            value={`¥${stats.todaySales.toLocaleString()}`}
                            subtitle="本日貸出中予約の決済合計"
                            icon={<TodayIcon />}
                            color="#3b82f6"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} lg={4} {...{} as any}>
                        <StatCard
                            title="今月の売上 (Square)"
                            value={`¥${stats.thisMonthSales.toLocaleString()}`}
                            subtitle={`${dayjs().format('M')}月の確定決済合計`}
                            icon={<TrendingUpIcon />}
                            color="#10b981"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} lg={4} {...{} as any}>
                        <StatCard
                            title="今月の売上予想"
                            value={`¥${stats.thisMonthExpectedSales.toLocaleString()}`}
                            subtitle="今月貸出開始の予約合計金額"
                            icon={<AssessmentIcon />}
                            gradient="linear-gradient(135deg, #6366f1 0%, #4338ca 100%)"
                        />
                    </Grid>
                </Grid>

                <Typography variant="h5" sx={{ fontWeight: 800, color: '#1e293b', mb: 3, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <FactCheckIcon color="primary" /> 今月の反響
                </Typography>

                <Grid container spacing={{ xs: 1.5, md: 3 }}>
                    <Grid item xs={12} lg={8} {...{} as any}>
                        <Paper sx={{ borderRadius: 4, overflow: 'hidden', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)' }}>
                            <TableContainer>
                                <Table>
                                    <TableHead sx={{ bgcolor: '#f1f5f9' }}>
                                        <TableRow>
                                            <TableCell sx={{ color: '#475569', fontWeight: 600 }}>媒体名</TableCell>
                                            <TableCell align="right" sx={{ color: '#475569', fontWeight: 600 }}>件数</TableCell>
                                            <TableCell align="right" sx={{ color: '#475569', fontWeight: 600 }}>金額</TableCell>
                                            <TableCell align="right" sx={{ color: '#475569', fontWeight: 600 }}>平均単価</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {stats.mediaArray.length > 0 ? stats.mediaArray.map((media: any) => (
                                            <TableRow key={media.name} hover>
                                                <TableCell sx={{ fontWeight: 500 }}>{media.name}</TableCell>
                                                <TableCell align="right">{media.count}件</TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 600 }}>¥{media.amount.toLocaleString()}</TableCell>
                                                <TableCell align="right" sx={{ color: '#64748b' }}>
                                                    ¥{Math.round(media.amount / media.count).toLocaleString()}
                                                </TableCell>
                                            </TableRow>
                                        )) : (
                                            <TableRow>
                                                <TableCell colSpan={4} align="center" sx={{ py: 3, color: '#94a3b8' }}>
                                                    データがありません
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} lg={4} {...{} as any}>
                        <Card sx={{ borderRadius: 4, height: '100%', bgcolor: '#fff', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)' }}>
                            <CardContent>
                                <Typography variant="h6" sx={{ fontWeight: 700, mb: 3 }}>
                                    反響サマリー
                                </Typography>
                                <Box sx={{ mb: 4 }}>
                                    <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mb: 1 }}>総件数</Typography>
                                    <Typography variant="h4" sx={{ fontWeight: 800 }}>{stats.responseCount} <Typography component="span" variant="h6">件</Typography></Typography>
                                </Box>
                                <Box sx={{ mb: 4 }}>
                                    <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mb: 1 }}>総金額</Typography>
                                    <Typography variant="h4" sx={{ fontWeight: 800 }}>¥{stats.responseAmount.toLocaleString()}</Typography>
                                </Box>
                                <Divider sx={{ my: 3 }} />
                                <Typography variant="body2" sx={{ color: '#64748b', fontStyle: 'italic' }}>
                                    ※今月中に「予約」が行われた（booking_dateが今月）
                                    確定済みのデータを集計しています。
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: { xs: 3, md: 6 }, mb: { xs: 1.5, md: 3 }, flexWrap: 'wrap', gap: 1 }}>
                    <Typography variant="h5" sx={{ fontWeight: 800, color: '#1e293b', display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <TrendingUpIcon color="primary" /> 反響（デイリー）
                    </Typography>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={onlyValidResponses}
                                onChange={(e: any) => setOnlyValidResponses(e.target.checked)}
                                color="primary"
                            />
                        }
                        label={<Typography variant="body2" sx={{ fontWeight: 600, color: '#64748b' }}>有効な予約のみ表示 (booked, in_use, done)</Typography>}
                    />
                </Box>

                <Paper sx={{ borderRadius: { xs: 2, md: 4 }, overflow: 'hidden', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)', mb: { xs: 3, md: 6 } }}>
                    <TableContainer sx={{ maxHeight: 500 }}>
                        <Table stickyHeader size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ bgcolor: '#f1f5f9', fontWeight: 600, width: 100 }}>日付</TableCell>
                                    <TableCell align="right" sx={{ bgcolor: '#f1f5f9', fontWeight: 600 }}>件数</TableCell>
                                    <TableCell align="right" sx={{ bgcolor: '#f1f5f9', fontWeight: 600 }}>金額</TableCell>
                                    <TableCell align="right" sx={{ bgcolor: '#f1f5f9', fontWeight: 600 }}>平均単価</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {stats.dailyResponseArray.map((day: any) => (
                                    <TableRow
                                        key={day.date}
                                        hover
                                        onClick={() => setModalData({ open: true, date: day.fullDate, list: day.reservations })}
                                        sx={{ cursor: day.count > 0 ? 'pointer' : 'default', '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' } }}
                                    >
                                        <TableCell sx={{
                                            fontWeight: 500,
                                            color: day.isSunday ? '#ef4444' : day.isSaturday ? '#3b82f6' : 'inherit',
                                            bgcolor: day.isWeekend ? 'rgba(0,0,0,0.02)' : 'transparent'
                                        }}>
                                            {day.date} ({day.dayOfWeek})
                                        </TableCell>
                                        <TableCell align="right">{day.count}件</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 600 }}>¥{day.amount.toLocaleString()}</TableCell>
                                        <TableCell align="right" sx={{ color: '#64748b' }}>
                                            ¥{day.avg.toLocaleString()}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
                </>
                )}
            </Box>

            {/* Daily Reservations Modal */}
            <Dialog
                open={modalData.open}
                onClose={() => setModalData(prev => ({ ...prev, open: false }))}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: { borderRadius: 3, boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)' }
                }}
            >
                <DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <FormatListBulletedIcon color="primary" />
                        <Typography variant="h6" sx={{ fontWeight: 800 }}>
                            {modalData.date} の反響リスト
                        </Typography>
                    </Box>
                    <IconButton onClick={() => setModalData(prev => ({ ...prev, open: false }))} size="small">
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers sx={{ p: 0 }}>
                    <TableContainer>
                        <Table size="small">
                            <TableHead sx={{ bgcolor: '#f8fafc' }}>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 600 }}>媒体</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>名前</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>ステータス</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 600 }}>金額</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>期間</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {modalData.list.map((r: any, idx: number) => (
                                    <TableRow key={r.id || idx} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`/reservations?res=${r.id}`)}>
                                        <TableCell sx={{ py: 1.5 }}>
                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                {r.contact_media || r.detail?.contact_media || '-'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                {r.customer_name || r.name || r.detail?.name || '名称不明'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Box sx={{
                                                display: 'inline-block',
                                                px: 1,
                                                py: 0.25,
                                                borderRadius: 1,
                                                fontSize: '0.75rem',
                                                fontWeight: 800,
                                                bgcolor: (String(r.status) === '2' || String(r.status).toLowerCase() === 'done') ? '#dcfce7' :
                                                    (String(r.status) === '1' || String(r.status).toLowerCase() === 'booked') ? '#dbeafe' : '#f1f5f9',
                                                color: (String(r.status) === '2' || String(r.status).toLowerCase() === 'done') ? '#166534' :
                                                    (String(r.status) === '1' || String(r.status).toLowerCase() === 'booked') ? '#1e40af' : '#475569'
                                            }}>
                                                {r.status_name || r.status}
                                            </Box>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography variant="body2" sx={{ fontWeight: 800 }}>
                                                ¥{(Number(r.sum_price) || Number(r.detail?.sum_price) || 0).toLocaleString()}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="caption" color="text.secondary">
                                                {dayjs(r.start_date).format('M/D')} 〜 {dayjs(r.end_date).format('M/D')}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </DialogContent>
                <DialogActions sx={{ p: 2, bgcolor: '#f8fafc' }}>
                    <Button onClick={() => setModalData(prev => ({ ...prev, open: false }))} variant="outlined" sx={{ borderRadius: 2 }}>
                        閉じる
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default Dashboard;
